import { networkIconFor } from '@alison/ui';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Fragment, type KeyboardEvent, type ReactNode } from 'react';
import { ThumbnailPreview } from '../creatives/ThumbnailPreview.js';
import { CHECKBOX_SX } from './Pill.js';
import {
  cellOf,
  deltaOf,
  improvementOf,
  metricsOf,
  type ResultRow,
  spendOf,
  type TableColumn,
} from './resultRows.js';

import type { ResultSelection } from './workflows.types.js';

export const METRIC_LABEL_SX = { fontSize: 10, fontWeight: 600, color: 'text.disabled' };

const NEUTRAL_CHIP = {
  px: 1.5,
  py: 0.5,
  borderRadius: 1,
  bgcolor: 'background.platform',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const THUMB = 80;
const TYPE_BADGE_SX = (theme: Theme) => ({
  position: 'absolute',
  top: 8,
  left: 5,
  px: 1,
  py: 0.25,
  borderRadius: '999px',
  bgcolor: alpha(theme.palette.common.black, 0.6),
  color: 'common.white',
  fontSize: 9,
  fontWeight: 700,
  pointerEvents: 'none',
});
const NAME_W = 250;
const METRICS_MIN = 224;
// Fixed-minimum auto-fill tracks put every row's metrics at the same x whatever its cell
// count, and wrap them onto a second line rather than overlap on a laptop-width card.
const WIDE_SIZING = {
  nameToMetrics: { xs: 0, xl: 8 },
  metricGap: { xs: 2, xl: 4 },
  metricColumns: {
    xs: 'repeat(auto-fill, minmax(104px, 1fr))',
    xl: 'repeat(auto-fill, minmax(160px, 1fr))',
  },
};
// A change chip may carry no label of its own, so it wraps together with its metric.
const PAIR_SX = {
  gridColumn: 'span 2',
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  alignItems: 'flex-start',
};
const COMPACT_SIZING = {
  nameToMetrics: 0,
  metricGap: 2,
  metricColumns: 'repeat(auto-fill, minmax(104px, 1fr))',
};

interface MetricCell {
  key: string;
  label: string;
  text: string;
  sx: SxProps<Theme>;
}

interface CreativeRowsProps {
  rows: ResultRow[];
  columns: TableColumn[];
  selection?: ResultSelection;
  renderDetail?: (row: ResultRow) => ReactNode;
  // Drawn at the row's trailing edge; on phones it wraps to its own line below the name.
  renderAside?: (row: ResultRow) => ReactNode;
  onOpen?: (row: ResultRow) => void;
  // The design outlines a card whose work is done — a creative with a brief.
  accented?: boolean;
  // The drawer draws no card: its sections sit on the surface and are ruled off
  // from each other, so the row loses its chrome, padding and count.
  plain?: boolean;
  compact?: boolean;
  // A heading over each run of rows sharing a group; rows arrive already ordered by group.
  groupOf?: (row: ResultRow) => string | null;
}

function TypeBadge({ type }: { type: string | null }): ReactNode {
  if (type === null) return null;
  return <Box sx={TYPE_BADGE_SX}>{type.toUpperCase()}</Box>;
}

function subline(row: ResultRow): string | null {
  const spend = spendOf(row);
  const parts = [
    row.daysLive === null ? null : `${row.daysLive}d live`,
    spend === null ? null : `${spend} spend`,
  ].filter((part) => part !== null);
  return parts.length === 0 ? null : parts.join(' · ');
}

export function tone(improvement: boolean) {
  return (theme: Theme) => ({
    px: 1.5,
    py: 0.5,
    borderRadius: 1,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    bgcolor: alpha(improvement ? theme.palette.success.dark : theme.palette.error.dark, 0.1),
    color: improvement ? theme.palette.success.dark : theme.palette.error.dark,
  });
}

function Metric({ label, text, sx }: Omit<MetricCell, 'key'>): ReactNode {
  return (
    <Stack sx={{ gap: 0.5, alignItems: 'flex-start', minWidth: 0 }}>
      <Typography sx={METRIC_LABEL_SX}>{label}</Typography>
      <Box sx={sx}>{text}</Box>
    </Stack>
  );
}

// A metric and its change are two cells, not one. Nesting the change inside the
// metric it belongs to pairs them tightly and leaves the rest of the row spread.
function cellsOf(row: ResultRow, column: TableColumn): MetricCell[] {
  const value = cellOf(row, column);
  const improvement = improvementOf(row, column);
  const delta = deltaOf(row, column);
  const signed = delta === null && improvement !== null;
  // A percentage that carries its own improvement is a change (vs. avg, CTR change), so
  // the arrow carries its direction and the colour good/bad. Any other value is a level
  // (profit, spend): it keeps its own sign and gets no arrow.
  // ponytail: format is the only change marker the service sends; switch to an explicit flag if one appears.
  const isChange =
    signed &&
    metricsOf(row.values).find((entry) => entry.metric === column.name)?.format === 'percentage';
  const drop = value.startsWith('-');
  const head: MetricCell = {
    key: column.label,
    label: column.label.toUpperCase(),
    text: isChange ? `${drop ? '↓' : '↑'} ${drop ? value.slice(1) : value}` : value,
    sx: signed && improvement !== null ? tone(improvement) : NEUTRAL_CHIP,
  };
  if (delta === null) return [head];
  return [
    head,
    {
      key: `${column.label}-change`,
      label: delta.label?.toUpperCase() ?? '\u00a0',
      text: delta.text,
      sx: tone(delta.isImprovement),
    },
  ];
}

export function CreativeRows({
  rows,
  columns,
  selection,
  renderDetail,
  renderAside,
  onOpen,
  accented = false,
  plain = false,
  compact = false,
  groupOf,
}: CreativeRowsProps): ReactNode {
  const sizing = compact ? COMPACT_SIZING : WIDE_SIZING;
  const allSelected = selection !== undefined && selection.selectedIds.length === rows.length;
  const someSelected = selection !== undefined && selection.selectedIds.length > 0;

  return (
    <Stack sx={{ gap: 2 }} data-testid="creative-rows">
      {plain ? null : (
        <Stack
          direction="row"
          sx={{ px: 1, alignItems: 'center', justifyContent: 'space-between' }}
        >
          {selection === undefined ? (
            <Box />
          ) : (
            <Stack
              component="label"
              direction="row"
              sx={{ gap: 1, alignItems: 'center', cursor: 'pointer' }}
            >
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={selection.onToggleAll}
                sx={CHECKBOX_SX}
                slotProps={{ input: { 'aria-label': 'Select all' } }}
              />
              <Typography sx={{ fontSize: 13 }}>Select all</Typography>
            </Stack>
          )}
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: 'success.main',
            }}
          >
            {rows.length} {rows.length === 1 ? 'CREATIVE' : 'CREATIVES'}
          </Typography>
        </Stack>
      )}

      {rows.map((row, index) => {
        const group = groupOf?.(row) ?? null;
        const previous = index === 0 ? undefined : rows[index - 1];
        const heading =
          group !== null && (previous === undefined || groupOf?.(previous) !== group)
            ? group
            : null;
        const sub = subline(row);
        const cells = columns.map((column) => cellsOf(row, column));
        const NetworkIcon = networkIconFor(row.network);
        const open =
          onOpen !== undefined && row.creativeId !== null ? () => onOpen(row) : undefined;

        return (
          <Fragment key={row.id}>
            {heading === null ? null : (
              <Typography
                component="h3"
                data-testid="creative-group"
                sx={{ px: 1, pt: index === 0 ? 0 : 2, fontSize: 13, fontWeight: 600 }}
              >
                {heading}
              </Typography>
            )}
            <Stack
              sx={
                plain
                  ? {}
                  : {
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: accented ? 'success.dark' : 'divider',
                    }
              }
            >
              <Stack
                direction="row"
                data-testid={`result-row-${row.id}`}
                sx={[
                  {
                    gap: { xs: 2, lg: 3 },
                    px: plain ? 0 : { xs: 2, lg: 3 },
                    py: 3,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  },
                  open !== undefined && { cursor: 'pointer' },
                ]}
                {...(open === undefined
                  ? {}
                  : {
                      onClick: open,
                      onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          open();
                        }
                      },
                      tabIndex: 0,
                    })}
              >
                {selection === undefined ? null : (
                  <Checkbox
                    size="small"
                    checked={selection.selectedIds.includes(row.id)}
                    onChange={() => selection.onToggle(row.id)}
                    onClick={(event) => event.stopPropagation()}
                    sx={CHECKBOX_SX}
                    slotProps={{ input: { 'aria-label': `Select ${row.label}` } }}
                  />
                )}

                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <ThumbnailPreview
                    name={row.label}
                    size={THUMB}
                    thumbnailUrl={row.thumbnailUrl}
                    previewUrl={row.previewUrl}
                    duration={row.duration}
                  />
                  <TypeBadge type={row.type} />
                </Box>

                <Stack
                  sx={
                    renderAside === undefined
                      ? { flex: `0 1 ${NAME_W}px`, minWidth: 120, gap: 0.5 }
                      : { flex: { xs: '1 1 0', sm: `0 1 ${NAME_W}px` }, minWidth: 0, gap: 0.5 }
                  }
                >
                  <Tooltip title={row.label}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.label}
                    </Typography>
                  </Tooltip>
                  {sub === null && NetworkIcon === undefined ? null : (
                    <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center' }}>
                      {NetworkIcon === undefined ? null : (
                        <NetworkIcon
                          titleAccess={row.network ?? undefined}
                          sx={{ fontSize: 14, flexShrink: 0 }}
                        />
                      )}
                      {sub === null ? null : (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          {sub}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Stack>

                {renderAside !== undefined && cells.length === 0 ? null : (
                  <Box
                    sx={{
                      ml: sizing.nameToMetrics,
                      // Below room for one metric/change pair, metrics drop under the name.
                      flex: `1 1 ${METRICS_MIN}px`,
                      minWidth: 0,
                      display: 'grid',
                      columnGap: sizing.metricGap,
                      rowGap: 2,
                      gridTemplateColumns: sizing.metricColumns,
                      alignItems: 'flex-start',
                    }}
                  >
                    {cells.map((pair) =>
                      pair.length === 1 ? (
                        pair.map(({ key, ...cell }) => <Metric key={key} {...cell} />)
                      ) : (
                        <Box key={pair[0]?.key} sx={PAIR_SX}>
                          {pair.map(({ key, ...cell }) => (
                            <Metric key={key} {...cell} />
                          ))}
                        </Box>
                      ),
                    )}
                  </Box>
                )}

                {renderAside === undefined ? null : (
                  <Box
                    sx={{
                      ml: 'auto',
                      flexShrink: 0,
                      flexBasis: { xs: '100%', sm: 'auto' },
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {renderAside(row)}
                  </Box>
                )}
              </Stack>
              {renderDetail?.(row)}
            </Stack>
          </Fragment>
        );
      })}
    </Stack>
  );
}
